import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import CardGridContainer from "../layouts/CardGridContainer";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import AcademicYearCard from "../components/academicYears/AcademicYearCard";
import CreateAcademicYearModal from "../components/academicYears/CreateAcademicYearModal";
import UpdateAcademicYearModal from "../components/academicYears/UpdateAcademicYearModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getAcademicYears,
  deleteAcademicYear,
  resetAcademicYearServerStatus,
} from "../redux/actions/academicYearActions";
import { AcademicYearResponseDto } from "../shared/dtos/academicYear/AcademicYearResponseDto";
import { ACADEMIC_YEAR_STATUS_OPTIONS } from "../shared/data/enumOptions";

const AcademicYearsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // Selectors
  const {
    items: academicYears,
    loadingFetchAll: loadingAcademicYears,
    errorFetchAll: errorLoadingAcademicYears,
    serverStatus,
    loadingCreate: loadingAcademicYearCreate,
    errorCreate: errorCreatingAcademicYear,
    loadingUpdate: loadingAcademicYearUpdate,
    errorUpdate: errorUpdatingAcademicYear,
    loadingDelete: loadingAcademicYearDelete,
  } = useAppSelector((state) => state.academicYear);

  // Local Filter States (Frontend only)
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] =
    useState<AcademicYearResponseDto | null>(null);

  // Permissions
  const canCreate = hasPermission("academic_year:create");
  const canUpdate = hasPermission("academic_year:update");
  const canDelete = hasPermission("academic_year:delete");

  // Initial Fetch
  const fetchYears = useCallback((): void => {
    dispatch(getAcademicYears());
  }, [dispatch]);

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  // Frontend Filter Logic
  const filteredAcademicYears = useMemo(() => {
    return academicYears.filter((year) => {
      const matchesSearch = year.name
        .toLowerCase()
        .includes(searchTerm.trim().toLowerCase());
      const matchesStatus = statusFilter
        ? year.academicYearStatus === statusFilter
        : true;

      return matchesSearch && matchesStatus;
    });
  }, [academicYears, searchTerm, statusFilter]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingAcademicYearCreate) setIsCreateModalOpen(false);
  }, [loadingAcademicYearCreate]);

  const handleOpenUpdate = (year: AcademicYearResponseDto): void => {
    setSelectedYear(year);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingAcademicYearUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedYear(null);
    }
  }, [loadingAcademicYearUpdate]);

  const handleOpenDelete = (year: AcademicYearResponseDto): void => {
    setSelectedYear(year);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingAcademicYearDelete) {
      setIsDeleteModalOpen(false);
      setSelectedYear(null);
    }
  }, [loadingAcademicYearDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedYear) {
      dispatch(deleteAcademicYear(selectedYear.id));
    }
  };

  // Server Status Handling
  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;
    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetAcademicYearServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Academic Years"
      subtitle="Manage school-wide academic calendars and sessions."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            New Academic Year
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search academic years..."
      >
        <AppSelectFilter
          label="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={ACADEMIC_YEAR_STATUS_OPTIONS}
          allLabel="All Statuses"
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingAcademicYears}
        error={errorLoadingAcademicYears}
        itemCount={filteredAcademicYears.length}
        emptyMessage="No academic years match your criteria"
        onRetry={fetchYears}
      >
        {filteredAcademicYears.map((year) => (
          <AcademicYearCard
            key={year.id}
            academicYear={year}
            onEdit={canUpdate ? () => handleOpenUpdate(year) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(year) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* MODALS */}
      {isCreateModalOpen && (
        <CreateAcademicYearModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingAcademicYearCreate}
          error={errorCreatingAcademicYear}
        />
      )}

      {isUpdateModalOpen && selectedYear && (
        <UpdateAcademicYearModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          academicYear={selectedYear}
          loading={loadingAcademicYearUpdate}
          error={errorUpdatingAcademicYear}
        />
      )}

      {isDeleteModalOpen && selectedYear && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Academic Year"
          description={`Are you sure you want to delete the academic year "${selectedYear.name}"? This action cannot be undone.`}
          loading={loadingAcademicYearDelete}
        />
      )}
    </PageContainer>
  );
};

export default AcademicYearsPage;
